import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

export interface SeoConfig {
  /** Bare page title; "| SEM Nexus" suffix added automatically unless title already contains it */
  title: string;
  description?: string;
  /** Path beginning with '/' or absolute URL. Defaults to current path if available. */
  canonical?: string;
  /** Absolute URL of the social-share image. Falls back to brand default. */
  image?: string;
  imageAlt?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  /** Set true on conversion / thank-you pages so search engines don't index them. */
  noindex?: boolean;
}

export interface BreadcrumbItem {
  name: string;
  /** Absolute or absolute-path URL. Omit on the current page (terminal crumb). */
  url?: string;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly SITE_NAME = 'SEM Nexus';
  private readonly BASE_URL = 'https://semnexus.com';
  private readonly TWITTER_HANDLE = '@semnexusdigital';
  // Fallback OG image used when a page doesn't set its own. Brand-neutral.
  private readonly DEFAULT_IMAGE = 'https://semnexus.com/images/shared/backgrounds/background-3.avif';
  // WP Yoast emitted this exact description on every page — mirror it for cutover invisibility.
  private readonly WEBSITE_DESC = 'Startup App Experts';
  // Yoast-mirrored logo. The WP version points at an old EC2 IP; we use the
  // semnexus.com WP path which the legacy nginx still proxies to Lightsail.
  private readonly LOGO_URL =
    'https://semnexus.com/wp-content/uploads/2022/04/cropped-SemNexusLogoHorizontal-WhiteType-1.png';

  // Social profiles (sameAs in Organization schema). Mirrors Yoast.
  private readonly SAME_AS = [
    'https://www.facebook.com/people/SEMnexus/100065162281996/',
    'https://x.com/semnexusdigital',
    'https://www.instagram.com/semnexus_digital/',
  ];

  // Single in-memory graph, split into global (Organization, WebSite — survive
  // SPA navigation) and per-page (WebPage, Article, Breadcrumb, ImageObject,
  // Person, Service, FAQ — cleared on each `apply()` to prevent leaking the
  // previous route's entities into the new page). Both maps merge on emit.
  private globalNodes: Map<string, Record<string, any>> = new Map();
  private pageNodes: Map<string, Record<string, any>> = new Map();

  constructor(
    private titleSrv: Title,
    private metaSrv: Meta,
    @Inject(DOCUMENT) private doc: Document,
  ) {}

  apply(cfg: SeoConfig): void {
    const fullTitle = cfg.title.includes(this.SITE_NAME) ? cfg.title : `${cfg.title} | ${this.SITE_NAME}`;
    this.titleSrv.setTitle(fullTitle);

    const canonicalPath = cfg.canonical ?? this.currentPath();
    const fullUrl = this.absoluteUrl(canonicalPath);
    const image = cfg.image && cfg.image.length > 0 ? cfg.image : this.DEFAULT_IMAGE;
    const type = cfg.type ?? 'website';

    this.setNameMeta('description', cfg.description);

    // Open Graph
    this.setPropMeta('og:title', fullTitle);
    this.setPropMeta('og:description', cfg.description);
    this.setPropMeta('og:url', fullUrl);
    this.setPropMeta('og:type', type);
    this.setPropMeta('og:site_name', this.SITE_NAME);
    this.setPropMeta('og:image', image);
    this.setPropMeta('og:image:alt', cfg.imageAlt);

    if (type === 'article') {
      this.setPropMeta('article:published_time', cfg.publishedTime);
      this.setPropMeta('article:modified_time', cfg.modifiedTime);
      this.setPropMeta('article:author', cfg.author);
    } else {
      this.removeMeta('property="article:published_time"');
      this.removeMeta('property="article:modified_time"');
      this.removeMeta('property="article:author"');
    }

    // Twitter Card
    this.setNameMeta('twitter:card', 'summary_large_image');
    this.setNameMeta('twitter:title', fullTitle);
    this.setNameMeta('twitter:description', cfg.description);
    this.setNameMeta('twitter:image', image);
    this.setNameMeta('twitter:site', this.TWITTER_HANDLE);

    // Canonical
    this.setCanonical(fullUrl);

    // Robots — noindex,nofollow when explicitly requested; otherwise remove the tag
    // so the page is indexable by default.
    if (cfg.noindex) {
      this.setNameMeta('robots', 'noindex,nofollow');
    } else {
      this.removeMeta('name="robots"');
    }

    // Clear all per-page entities from the previous route so the @graph only
    // describes the current page.
    this.pageNodes.clear();

    // Always emit a WebPage node for this URL so the @graph has a primary
    // page entity that BreadcrumbList and Article can reference via @id.
    this.upsertPage(fullUrl, {
      '@type': 'WebPage',
      '@id': fullUrl,
      url: fullUrl,
      name: fullTitle,
      isPartOf: { '@id': `${this.BASE_URL}/#website` },
      inLanguage: 'en-US',
      potentialAction: [{ '@type': 'ReadAction', target: [fullUrl] }],
      ...(cfg.publishedTime && { datePublished: cfg.publishedTime }),
      ...(cfg.modifiedTime && { dateModified: cfg.modifiedTime }),
    });

    this.emitGraph();
  }

  private setNameMeta(name: string, content: string | undefined): void {
    if (content == null || content === '') {
      this.metaSrv.removeTag(`name="${name}"`);
      return;
    }
    this.metaSrv.updateTag({ name, content });
  }

  private setPropMeta(property: string, content: string | undefined): void {
    if (content == null || content === '') {
      this.metaSrv.removeTag(`property="${property}"`);
      return;
    }
    this.metaSrv.updateTag({ property, content });
  }

  private removeMeta(selector: string): void {
    this.metaSrv.removeTag(selector);
  }

  private setCanonical(href: string): void {
    let link = this.doc.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  private currentPath(): string {
    try {
      return this.doc.location?.pathname ?? '/';
    } catch {
      return '/';
    }
  }

  private absoluteUrl(pathOrUrl: string): string {
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return `${this.BASE_URL}${path}`;
  }

  // ---- @graph helpers ----

  /** Upsert a per-page node. Cleared on each new `apply()`. */
  private upsertPage(key: string, data: Record<string, any>): void {
    const existing = this.pageNodes.get(key) ?? {};
    this.pageNodes.set(key, { ...existing, ...data });
  }

  /** Upsert a global node. Persists across SPA navigation. */
  private upsertGlobal(key: string, data: Record<string, any>): void {
    const existing = this.globalNodes.get(key) ?? {};
    this.globalNodes.set(key, { ...existing, ...data });
  }

  /** Re-serialize all graph nodes as a single <script id="ld-graph">. */
  private emitGraph(): void {
    const payload = {
      '@context': 'https://schema.org',
      '@graph': [...this.globalNodes.values(), ...this.pageNodes.values()],
    };
    let el = this.doc.head.querySelector<HTMLScriptElement>('script#ld-graph');
    if (!el) {
      el = this.doc.createElement('script');
      el.setAttribute('type', 'application/ld+json');
      el.setAttribute('id', 'ld-graph');
      this.doc.head.appendChild(el);
    }
    el.textContent = JSON.stringify(payload);

    // Remove the legacy per-id scripts left over from the pre-@graph
    // SeoService API so we don't emit duplicate Organization / WebSite /
    // Article / Breadcrumb entries (Yoast's site has exactly one).
    for (const stale of ['ld-organization', 'ld-website', 'ld-article', 'ld-breadcrumb']) {
      const e = this.doc.head.querySelector<HTMLScriptElement>(`script#${stale}`);
      if (e) e.remove();
    }
  }

  /**
   * Backward-compatible legacy API. Now an upsert into the shared @graph.
   * For ids in { organization, website, article, breadcrumb } we use the
   * Yoast-canonical @id pattern. For everything else (service, faq,
   * case-study, …) we store under a synthetic key so multiple per-page
   * Service/FAQ nodes can coexist.
   */
  setJsonLd(id: string, data: object): void {
    const key = `local:${id}`;
    this.pageNodes.set(key, data as Record<string, any>);
    this.emitGraph();
  }

  removeJsonLd(id: string): void {
    this.pageNodes.delete(`local:${id}`);
    this.emitGraph();
  }

  /** Emit BreadcrumbList JSON-LD and wire the current WebPage's `breadcrumb` ref. */
  setBreadcrumbs(items: BreadcrumbItem[]): void {
    const pageUrl = this.absoluteUrl(this.currentPath());
    const id = `${pageUrl}#breadcrumb`;
    this.upsertPage(id, {
      '@type': 'BreadcrumbList',
      '@id': id,
      itemListElement: items.map((item, i) => {
        const node: Record<string, any> = {
          '@type': 'ListItem',
          position: i + 1,
          name: item.name,
        };
        if (item.url) node['item'] = this.absoluteUrl(item.url);
        return node;
      }),
    });
    // Link from the WebPage if present.
    const page = this.pageNodes.get(pageUrl);
    if (page) {
      page['breadcrumb'] = { '@id': id };
      this.pageNodes.set(pageUrl, page);
    }
    this.emitGraph();
  }

  /** Emit Article + ImageObject + Person nodes, wired into the WebPage via @id. */
  setArticle(cfg: {
    title: string;
    description?: string;
    slug: string;
    image?: string;
    datePublished?: string;
    dateModified?: string;
    author?: string;
    /** Optional categories — first one becomes articleSection (matches Yoast). */
    section?: string;
    /** Optional word count — Yoast emits this on every Article node. */
    wordCount?: number;
  }): void {
    const url = this.absoluteUrl(`/${cfg.slug}`);
    const articleId = `${url}#article`;
    const imageId = `${url}#primaryimage`;
    const personId = this.personId(cfg.author);
    const image = cfg.image && cfg.image.length > 0 ? cfg.image : this.DEFAULT_IMAGE;

    // Article
    this.upsertPage(articleId, {
      '@type': 'Article',
      '@id': articleId,
      isPartOf: { '@id': url },
      author: cfg.author ? { '@id': personId, name: cfg.author } : undefined,
      headline: cfg.title,
      datePublished: cfg.datePublished,
      dateModified: cfg.dateModified || cfg.datePublished,
      mainEntityOfPage: { '@id': url },
      ...(cfg.wordCount != null && { wordCount: cfg.wordCount }),
      publisher: { '@id': `${this.BASE_URL}/#organization` },
      image: { '@id': imageId },
      thumbnailUrl: image,
      ...(cfg.section && { articleSection: [cfg.section] }),
      inLanguage: 'en-US',
    });

    // ImageObject (primary image)
    this.upsertPage(imageId, {
      '@type': 'ImageObject',
      inLanguage: 'en-US',
      '@id': imageId,
      url: image,
      contentUrl: image,
    });

    // Person (author)
    if (cfg.author) {
      this.upsertPage(personId, {
        '@type': 'Person',
        '@id': personId,
        name: cfg.author,
      });
    }

    // Decorate the WebPage with primary-image refs (Yoast does this).
    const page = this.pageNodes.get(url);
    if (page) {
      page['primaryImageOfPage'] = { '@id': imageId };
      page['image'] = { '@id': imageId };
      page['thumbnailUrl'] = image;
      page['datePublished'] = cfg.datePublished;
      page['dateModified'] = cfg.dateModified || cfg.datePublished;
      this.pageNodes.set(url, page);
    }

    this.emitGraph();
  }

  /**
   * Emit Organization + WebSite JSON-LD into the @graph.
   * Called once from the root App component on bootstrap.
   * Mirrors Yoast's WP output for byte-near-equivalent crawler behavior.
   */
  setGlobalSchemas(): void {
    // WebSite (Yoast-mirror)
    this.upsertGlobal(`${this.BASE_URL}/#website`, {
      '@type': 'WebSite',
      '@id': `${this.BASE_URL}/#website`,
      url: `${this.BASE_URL}/`,
      name: this.SITE_NAME,
      description: this.WEBSITE_DESC,
      publisher: { '@id': `${this.BASE_URL}/#organization` },
      potentialAction: [
        {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${this.BASE_URL}/?s={search_term_string}`,
          },
          'query-input': {
            '@type': 'PropertyValueSpecification',
            valueRequired: true,
            valueName: 'search_term_string',
          },
        },
      ],
      inLanguage: 'en-US',
    });

    // Organization (Yoast-mirror — same thin shape as the WP site)
    this.upsertGlobal(`${this.BASE_URL}/#organization`, {
      '@type': 'Organization',
      '@id': `${this.BASE_URL}/#organization`,
      name: this.SITE_NAME,
      url: `${this.BASE_URL}/`,
      logo: {
        '@type': 'ImageObject',
        inLanguage: 'en-US',
        '@id': `${this.BASE_URL}/#/schema/logo/image/`,
        url: this.LOGO_URL,
        contentUrl: this.LOGO_URL,
        width: 512,
        height: 512,
        caption: this.SITE_NAME,
      },
      image: { '@id': `${this.BASE_URL}/#/schema/logo/image/` },
      sameAs: this.SAME_AS,
    });

    this.emitGraph();
  }

  /** Deterministic Person @id matching Yoast's per-author URL slot. */
  private personId(authorName: string | undefined): string {
    const slug = (authorName || 'unknown').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return `${this.BASE_URL}/#/schema/person/${slug}`;
  }
}
